/**
 * Simulador público de pricing — "¿Cuánto te cuesta?".
 *
 * Componente reutilizable. Recibe vertical + GMV anual + ticket medio,
 * calcula coste anual con Pagafácil y lo compara con las cinco
 * alternativas más conocidas del mercado.
 *
 * Cero estado externo · 100 % cálculo local · sin llamada a API.
 */
import { useMemo, useState } from 'react';


type VerticalKey =
  | 'papeleta_sitio'
  | 'raffle_share'
  | 'group_dinner'
  | 'membership_quota'
  | 'ticket_share'
  | 'group_gift'
  | 'porra'
  | 'event_crowdfund';


interface VerticalSpec {
  key: VerticalKey;
  label: string;
  sub: string;
  rate: number; // comisión Pagafácil
  gmv: number;  // default GMV anual
  ticket: number; // default ticket medio
}


const VERTICALS: VerticalSpec[] = [
  { key: 'membership_quota', label: 'Cuota de socios', sub: '1,5 % · AMPA, peña, club', rate: 0.015, gmv: 3000, ticket: 30 },
  { key: 'papeleta_sitio', label: 'Papeleta de sitio', sub: '2,0 % · hermandad', rate: 0.020, gmv: 50000, ticket: 100 },
  { key: 'ticket_share', label: 'Entradas en grupo', sub: '2,0 % · concierto, festival', rate: 0.020, gmv: 3000, ticket: 30 },
  { key: 'raffle_share', label: 'Sorteo benéfico', sub: '2,5 % · ONG, AMPA, peña', rate: 0.025, gmv: 5000, ticket: 5 },
  { key: 'group_dinner', label: 'Cena con OCR', sub: '2,5 % · dividir cuenta', rate: 0.025, gmv: 1500, ticket: 25 },
  { key: 'group_gift', label: 'Regalo conjunto', sub: '3,0 % · cumple, despedida', rate: 0.030, gmv: 300, ticket: 25 },
  { key: 'porra', label: 'Porra deportiva', sub: '3,0 % · Liga, Champions', rate: 0.030, gmv: 500, ticket: 5 },
  { key: 'event_crowdfund', label: 'Crowdfunding mini', sub: '5,0 % · all-or-nothing', rate: 0.050, gmv: 5000, ticket: 25 },
];


function formatEUR(n: number, decimals = 0): string {
  return n.toLocaleString('es-ES', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }) + ' €';
}


interface CompetitorRow {
  name: string;
  formula: string;
  cost: number;
  note?: string;
  warning?: boolean;
}


function competitorCosts(gmv: number, ops: number): CompetitorRow[] {
  return [
    {
      name: 'PayPal Money Pools',
      formula: '1,9 % + 0,30 €/op',
      cost: gmv * 0.019 + ops * 0.30,
      note: 'sin DNI nominativo · UX heredada',
    },
    {
      name: 'GoFundMe',
      formula: '2,9 % + 0,30 €/op',
      cost: gmv * 0.029 + ops * 0.30,
      note: 'orientado a EEUU · sin Bizum',
    },
    {
      name: 'Verkami / Goteo',
      formula: '5 % + IVA + 0,30 €/op',
      cost: gmv * 0.0605 + ops * 0.30,
      note: 'solo crowdfunding · no es directo',
    },
    {
      name: 'Stripe directo (DIY)',
      formula: '1,4 % + 0,25 €/op',
      cost: gmv * 0.014 + ops * 0.25,
      note: 'sin panel · sin emails · sin OCR · sin nada',
      warning: true,
    },
    {
      name: 'Bizum manual del tesorero',
      formula: '0 € + horas perseguidas',
      cost: estimateTesoreroHours(ops) * 20,
      note: `~${estimateTesoreroHours(ops)} h/año a 20 €/h en tiempo perdido`,
    },
  ];
}


function estimateTesoreroHours(ops: number): number {
  // 1 hora cada 20 cobros + 8 h fijas de cuadre Excel.
  // Tope sensato en 120 h/año para no inflar.
  const h = Math.min(120, Math.max(8, Math.round(ops / 20) + 8));
  return h;
}


export default function PricingSimulator({
  compact = false,
}: {
  compact?: boolean;
}) {
  const [verticalKey, setVerticalKey] = useState<VerticalKey>('papeleta_sitio');
  const v = VERTICALS.find((x) => x.key === verticalKey)!;
  const [gmv, setGmv] = useState<number>(v.gmv);
  const [ticket, setTicket] = useState<number>(v.ticket);
  const [premium, setPremium] = useState<boolean>(false);
  const [onlinePct, setOnlinePct] = useState<number>(100);

  // Cuando cambias de vertical, sincronizamos defaults.
  const onVertical = (k: VerticalKey) => {
    const nv = VERTICALS.find((x) => x.key === k)!;
    setVerticalKey(k);
    setGmv(nv.gmv);
    setTicket(nv.ticket);
  };

  const ops = useMemo(() => {
    if (!ticket || ticket <= 0) return 0;
    return Math.max(1, Math.round(gmv / ticket));
  }, [gmv, ticket]);

  const onlineGmv = gmv * (onlinePct / 100);
  const ourRate = premium ? 0.01 : v.rate;
  const ourCost = onlineGmv * ourRate;
  // Premium solo tiene sentido si vas a procesar online · si todo es offline,
  // no contamos el premium.
  const premiumExtra = premium && onlinePct > 0 ? 12 * 9 : 0;
  const ourTotal = ourCost + premiumExtra;

  const competitors = useMemo(
    () => competitorCosts(onlineGmv, Math.max(1, Math.round(onlineGmv / Math.max(1, ticket)))),
    [onlineGmv, ticket],
  );

  const paypal = competitors.find((c) => c.name === 'PayPal Money Pools')!;
  const savingsVsPaypal = paypal.cost - ourTotal;

  return (
    <div className="bg-paper border border-line shadow-card rounded-card overflow-hidden">
      {!compact && (
        <div className="px-6 md:px-8 pt-7 pb-5 border-b border-line-soft">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-3">
            · ¿Cuánto te cuesta?
          </div>
          <h2 className="font-display font-bold text-[28px] md:text-[34px] leading-[1.04] tracking-[-0.02em] text-ink mb-2">
            Pon tus números.
          </h2>
          <p className="text-[14.5px] text-ink-700 max-w-[560px]">
            Te decimos lo que cuesta con nosotros y con cada alternativa que ya
            existe. Sin trampa.
          </p>
        </div>
      )}

      <div className="grid md:grid-cols-[1fr_1.15fr] divide-y md:divide-y-0 md:divide-x divide-line-soft">
        {/* Inputs */}
        <div className="p-6 md:p-8 space-y-6">
          <div>
            <label className="cc-label">¿Qué tipo de cobro?</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {VERTICALS.map((spec) => {
                const sel = verticalKey === spec.key;
                return (
                  <button
                    key={spec.key}
                    type="button"
                    onClick={() => onVertical(spec.key)}
                    className={
                      sel
                        ? 'text-left p-3 rounded-xl border-[1.5px] border-brand bg-brand-50 transition'
                        : 'text-left p-3 rounded-xl border-[1.5px] border-line-strong bg-paper hover:border-brand transition'
                    }
                  >
                    <div
                      className={
                        sel
                          ? 'font-display font-semibold text-[14.5px] text-brand-700 tracking-[-0.01em]'
                          : 'font-display font-semibold text-[14.5px] text-ink tracking-[-0.01em]'
                      }
                    >
                      {spec.label}
                    </div>
                    <div className="text-[11.5px] text-ink-500 mt-0.5 font-mono uppercase tracking-[0.06em]">
                      {spec.sub}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="cc-label">GMV anual estimado · €</label>
            <input
              type="number"
              min={100}
              step={100}
              value={gmv}
              onChange={(e) => setGmv(Math.max(0, parseInt(e.target.value) || 0))}
              className="cc-input"
            />
            <p className="cc-helper">Cuánto pasa por el cobro en total al año.</p>
          </div>

          <div>
            <label className="cc-label">Ticket medio por pagador · €</label>
            <input
              type="number"
              min={1}
              step={1}
              value={ticket}
              onChange={(e) => setTicket(Math.max(0, parseInt(e.target.value) || 0))}
              className="cc-input"
            />
            <p className="cc-helper">
              Estimamos {ops.toLocaleString('es-ES')} operaciones al año.
            </p>
          </div>

          <div>
            <div className="flex items-baseline justify-between mb-1.5">
              <label className="cc-label !mb-0">¿Cuántos pagan online?</label>
              <span className="font-display font-semibold text-[15px] text-brand tracking-[-0.01em]">
                {onlinePct}%
              </span>
            </div>
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={onlinePct}
              onChange={(e) => setOnlinePct(parseInt(e.target.value))}
              className="cc-range"
            />
            <p className="cc-helper">
              {onlinePct === 0
                ? '· Nadie usará el pago online. Cuesta 0 €.'
                : onlinePct === 100
                ? '· Todos pagarán por Bizum o tarjeta. Comisión sobre el 100 %.'
                : `· ${onlinePct} % por Bizum/tarjeta · el resto manual offline (gratis).`}
            </p>
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={premium}
              onChange={(e) => setPremium(e.target.checked)}
              className="mt-1 w-4 h-4 accent-brand"
            />
            <div>
              <div className="font-display font-semibold text-[15px] text-ink tracking-[-0.01em]">
                Plan Premium · 9 €/mes
              </div>
              <div className="text-[12.5px] text-ink-700">
                Baja la comisión a 1 % en todo + marca propia y exportable. Compensa a partir de ~6.000 €/año de GMV procesado online.
              </div>
            </div>
          </label>
        </div>

        {/* Resultado */}
        <div className="p-6 md:p-8 bg-surface">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-2">
            · Tu coste con Pagafácil
          </div>
          <div className="font-display font-bold text-[44px] md:text-[52px] text-olive-700 leading-none tracking-[-0.03em] mb-1">
            {formatEUR(ourTotal)}
          </div>
          {onlinePct === 0 ? (
            <p className="text-[13.5px] text-ink-700 mb-2">
              Sin pagos online procesados ·{' '}
              <strong className="text-olive-700">cuesta 0 €</strong>. Cobramos
              solo cuando alguien usa Bizum o tarjeta.
            </p>
          ) : premium ? (
            <p className="text-[13.5px] text-ink-700 mb-2">
              1 % de {formatEUR(onlineGmv)} + 108 € de Premium ={' '}
              <strong className="text-ink">{formatEUR(ourTotal)}</strong> al año.
            </p>
          ) : (
            <p className="text-[13.5px] text-ink-700 mb-2">
              {(v.rate * 100).toLocaleString('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}{' '}
              % de {formatEUR(onlineGmv)} ={' '}
              <strong className="text-ink">{formatEUR(ourCost)}</strong> al año.
            </p>
          )}
          {savingsVsPaypal > 0 && onlinePct > 0 && (
            <p className="text-[12.5px] text-olive-700 font-semibold">
              · Ahorras {formatEUR(savingsVsPaypal)} vs PayPal Money Pools.
            </p>
          )}

          {/* Banda freemium destacada */}
          <div className="mt-5 bg-olive-50 border border-[#C7D5A8] rounded-xl px-4 py-3">
            <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-olive-700 mb-1">
              · Gratis hasta que alguien pague
            </div>
            <p className="text-[13px] text-ink-700 leading-snug">
              Crear el cobro, dividir el ticket con la IA, generar los mensajes de WhatsApp y ver quién falta es <strong className="text-ink">siempre gratis</strong>. Solo cobramos cuando alguien usa el pago online.
            </p>
          </div>

          <div className="mt-6 border-t border-line-soft pt-5">
            <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-3">
              · Lo mismo con las alternativas
            </div>
            <ul className="space-y-3">
              {competitors.map((c) => {
                const diff = c.cost - ourTotal;
                return (
                  <li key={c.name} className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-display font-semibold text-[15px] text-ink tracking-[-0.01em] truncate">
                        {c.name}
                      </p>
                      <p className="text-[12px] text-ink-500 font-mono uppercase tracking-[0.06em]">
                        · {c.formula}
                      </p>
                      {c.note && (
                        <p className="text-[12px] text-ink-700 mt-0.5">{c.note}</p>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <p
                        className={
                          c.warning
                            ? 'font-display font-semibold text-[18px] text-amberCC-700 tracking-[-0.01em] leading-none'
                            : 'font-display font-semibold text-[18px] text-ink tracking-[-0.01em] leading-none'
                        }
                      >
                        {formatEUR(c.cost)}
                      </p>
                      {diff > 0 ? (
                        <p className="text-[11.5px] text-olive-700 font-semibold mt-1 font-mono uppercase tracking-[0.06em]">
                          · +{formatEUR(diff)}
                        </p>
                      ) : diff < 0 ? (
                        <p className="text-[11.5px] text-terra-700 font-semibold mt-1 font-mono uppercase tracking-[0.06em]">
                          · {formatEUR(diff)}
                        </p>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          <p className="mt-5 text-[12px] text-ink-500 leading-relaxed">
            · El pagador no ve ninguna de estas comisiones. La paga el
            organizador desde lo recaudado. <br />
            · "Bizum manual" estima ~{estimateTesoreroHours(ops)} h/año del
            tesorero a 20 €/h. Si tu tiempo no cuenta, el coste es cero — pero
            te toca perseguir.
          </p>
        </div>
      </div>

      <Style />
    </div>
  );
}


function Style() {
  return (
    <style>{`
      .cc-input{
        width:100%;
        padding:13px 15px;
        border:1.5px solid #DDD3C4;
        border-radius:11px;
        background:#FFFFFF;
        font-family:inherit;
        font-size:14.5px;
        color:#26201A;
        outline:none;
        transition:border-color .15s ease;
      }
      .cc-input:focus{ border-color:#1C5D82; }
      .cc-input::placeholder{ color:#A89D8E; }
      .cc-label{
        display:block;
        font-family:"Hanken Grotesk",sans-serif;
        font-size:12.5px;
        font-weight:700;
        letter-spacing:.02em;
        color:#4A4239;
        margin-bottom:7px;
      }
      .cc-helper{
        margin-top:6px;
        font-size:12px;
        color:#8A8073;
        line-height:1.4;
      }
      .cc-range{
        -webkit-appearance:none;
        appearance:none;
        width:100%;
        height:6px;
        background:#EAE1D2;
        border-radius:999px;
        outline:none;
        cursor:pointer;
      }
      .cc-range::-webkit-slider-thumb{
        -webkit-appearance:none;
        appearance:none;
        width:20px;
        height:20px;
        border-radius:50%;
        background:#1C5D82;
        border:3px solid #FFFFFF;
        box-shadow:0 1px 3px rgba(28,93,130,.4);
        cursor:pointer;
      }
      .cc-range::-moz-range-thumb{
        width:20px;
        height:20px;
        border-radius:50%;
        background:#1C5D82;
        border:3px solid #FFFFFF;
        box-shadow:0 1px 3px rgba(28,93,130,.4);
        cursor:pointer;
      }
    `}</style>
  );
}
